import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

import {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { useDebouncedValue } from '@src/packages/utils';

import { useCloseOnAntdOverlay } from '../hooks/useCloseOnAntdOverlay';

export interface SecondaryPanelConfig {
  title?: string;
  content?: ReactNode;
  width?: number;
  onClose?: (values: Record<string, unknown>) => void;
  topOffset?: number;
}

export interface MountSecondaryPanelConfig extends SecondaryPanelConfig {
  initialValues?: FilterValues;
}

export interface SecondaryPanelFiltersContextType {
  values: FilterValues;
  setValue: (props: SetValueProps) => void;
  setValues: (values: FilterValues) => void;
  reset: () => void;
}

export interface SecondaryPanelUIContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  panel: SecondaryPanelConfig;
  mountPanel: (panel: MountSecondaryPanelConfig) => void;
  updatePanel: (panel: SecondaryPanelConfig) => void;
  unmountPanel: () => void;
  contentNode: HTMLDivElement | null;
  setContentNode: (node: HTMLDivElement | null) => void;
  /** Page-level filter bar the rail aligns its top and header height to. */
  anchorNode: HTMLDivElement | null;
  setAnchorNode: Dispatch<SetStateAction<HTMLDivElement | null>>;
  /** DOM node inside the rail body; use with createPortal from a subtree that must keep app context. */
  railPortalHostEl: HTMLDivElement | null;
  setRailPortalHostEl: Dispatch<SetStateAction<HTMLDivElement | null>>;
}

export interface SecondaryPanelContextType
  extends SecondaryPanelFiltersContextType, SecondaryPanelUIContextType {}

export interface UseSecondaryPanelOptions {
  /** Debounce delay (ms) applied to `debouncedValues`. When omitted/0, `debouncedValues === values`. */
  debounceDelay?: number;
}

export interface UseSecondaryPanelReturn extends SecondaryPanelContextType {
  /** Filter values delayed by `debounceDelay`; use it to trigger fetches while `values` stays immediate. */
  debouncedValues: FilterValues;
}

const DEFAULT_VALUES: FilterValues = {};
export const DEFAULT_SECONDARY_PANEL_WIDTH = 360;
const DEFAULT_PANEL: SecondaryPanelConfig = {
  title: undefined,
  content: null,
  width: DEFAULT_SECONDARY_PANEL_WIDTH,
  onClose: undefined,
  topOffset: 0,
};

const SecondaryPanelFiltersContext = createContext<SecondaryPanelFiltersContextType | null>(null);
const SecondaryPanelUIContext = createContext<SecondaryPanelUIContextType | null>(null);

export interface SecondaryPanelProviderProps {
  children: ReactNode;
}

export const SecondaryPanelProvider: React.FC<SecondaryPanelProviderProps> = ({ children }) => {
  const [values, setValuesState] = useState<FilterValues>(DEFAULT_VALUES);
  const [isOpen, setIsOpen] = useState(false);
  const [panel, setPanel] = useState<SecondaryPanelConfig>(DEFAULT_PANEL);
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  const [anchorNode, setAnchorNode] = useState<HTMLDivElement | null>(null);
  const [railPortalHostEl, setRailPortalHostEl] = useState<HTMLDivElement | null>(null);

  const valuesRef = useRef(values);
  const panelRef = useRef(panel);
  const isOpenRef = useRef(isOpen);

  valuesRef.current = values;
  panelRef.current = panel;
  isOpenRef.current = isOpen;

  const setValue = useCallback(
    ({
      key,
      value,
      label,
      valueTranslated,
      valueType,
      clearValue,
      hideWhenCleared,
    }: SetValueProps) => {
      setValuesState(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          value,
          ...(label !== undefined ? { label: label || '' } : {}),
          ...(valueTranslated !== undefined ? { valueTranslated: valueTranslated || '' } : {}),
          ...(valueType !== undefined ? { valueType } : {}),
          ...(clearValue !== undefined ? { clearValue } : {}),
          ...(hideWhenCleared !== undefined ? { hideWhenCleared } : {}),
        },
      }));
    },
    [],
  );

  const setValues = useCallback((newValues: FilterValues) => {
    setValuesState(newValues);
  }, []);

  const reset = useCallback(() => {
    setValuesState(DEFAULT_VALUES);
  }, []);

  const notifyClose = useCallback(() => {
    panelRef.current.onClose?.(valuesRef.current as Record<string, unknown>);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (!isOpenRef.current) return;
    setIsOpen(false);
    notifyClose();
  }, [notifyClose]);

  useCloseOnAntdOverlay({ isOpenRef, close });

  const toggle = useCallback(() => {
    if (isOpenRef.current) {
      setIsOpen(false);
      notifyClose();
      return;
    }

    setIsOpen(true);
  }, [notifyClose]);

  const mountPanel = useCallback((nextPanel: MountSecondaryPanelConfig) => {
    setPanel({
      title: nextPanel.title,
      content: nextPanel.content,
      width: nextPanel.width ?? DEFAULT_SECONDARY_PANEL_WIDTH,
      onClose: nextPanel.onClose,
      topOffset: nextPanel.topOffset ?? DEFAULT_PANEL.topOffset,
    });
    setValuesState(nextPanel.initialValues ?? DEFAULT_VALUES);
    setIsOpen(false);
  }, []);

  const updatePanel = useCallback((nextPanel: SecondaryPanelConfig) => {
    setPanel(prev => ({
      ...prev,
      ...nextPanel,
      width: nextPanel.width ?? prev.width ?? DEFAULT_SECONDARY_PANEL_WIDTH,
    }));
  }, []);

  const unmountPanel = useCallback(() => {
    setPanel(DEFAULT_PANEL);
    setValuesState(DEFAULT_VALUES);
    setIsOpen(false);
    setRailPortalHostEl(null);
  }, []);

  const filtersContextValue = useMemo<SecondaryPanelFiltersContextType>(
    () => ({
      values,
      setValue,
      setValues,
      reset,
    }),
    [values, setValue, setValues, reset],
  );

  const uiContextValue = useMemo<SecondaryPanelUIContextType>(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      panel,
      mountPanel,
      updatePanel,
      unmountPanel,
      contentNode,
      setContentNode,
      anchorNode,
      setAnchorNode,
      railPortalHostEl,
      setRailPortalHostEl,
    }),
    [
      isOpen,
      open,
      close,
      toggle,
      panel,
      mountPanel,
      updatePanel,
      unmountPanel,
      contentNode,
      setContentNode,
      anchorNode,
      setAnchorNode,
      railPortalHostEl,
      setRailPortalHostEl,
    ],
  );

  return (
    <SecondaryPanelFiltersContext.Provider value={filtersContextValue}>
      <SecondaryPanelUIContext.Provider value={uiContextValue}>
        {children}
      </SecondaryPanelUIContext.Provider>
    </SecondaryPanelFiltersContext.Provider>
  );
};

export const useSecondaryPanelFilters = (): SecondaryPanelFiltersContextType => {
  const ctx = useContext(SecondaryPanelFiltersContext);

  if (!ctx) {
    throw new Error('useSecondaryPanelFilters must be used inside <SecondaryPanelProvider>');
  }

  return ctx;
};

export const useSecondaryPanelUI = (): SecondaryPanelUIContextType => {
  const ctx = useContext(SecondaryPanelUIContext);

  if (!ctx) {
    throw new Error('useSecondaryPanelUI must be used inside <SecondaryPanelProvider>');
  }

  return ctx;
};

export const useSecondaryPanel = (options?: UseSecondaryPanelOptions): UseSecondaryPanelReturn => {
  const filters = useSecondaryPanelFilters();
  const ui = useSecondaryPanelUI();

  const debouncedValues = useDebouncedValue(filters.values, options?.debounceDelay);

  return useMemo(
    () => ({
      ...filters,
      ...ui,
      debouncedValues,
    }),
    [filters, ui, debouncedValues],
  );
};
